import React, { useEffect, useState } from 'react';
import Loading from '../../custom/Loading';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { useAppTranslation } from '../../../i18n';
import { useObservePosIdentSession } from '../../../hooks/useObservePosIdentSession';
import { selectPosPhotoIdentIdentity } from '../../../app/slices/report.slice';
import { changeStep } from '../../../app/slices/step.slice';
import { CheckIntegrationStatus } from '../../../types/check/types';
import StepButton from '../StepButton';
import PosIdentConfirmModal from './PosIdentConfirmModal';

const TRANSLATION_PREFIX = 'steps.pos-ident-redirect';

type PendingAction = 'reopen' | 'back';

const PosIdentRedirect = () => {
	const { t } = useAppTranslation();
	const dispatch = useAppDispatch();
	useObservePosIdentSession();

	const identity = useAppSelector(selectPosPhotoIdentIdentity);
	const redirectUrl = identity?.metadata?.redirectUrl ?? identity?.metadata?.IdentificationUrl;
	const alreadyRedirected =
		identity?.metadata?.sessionRedirected === true || identity?.metadata?.isRedirected === true;
	const isProcessing = identity?.status === CheckIntegrationStatus.PROCESSING;

	const showRecovery = isProcessing && alreadyRedirected;

	const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);

	useEffect(() => {
		if (!isProcessing) setPendingAction(null);
	}, [isProcessing]);

	const confirmAction = () => {
		if (pendingAction === 'reopen' && redirectUrl) {
			window.location.href = redirectUrl;
		} else if (pendingAction === 'back') {
			dispatch(changeStep('BANK_AUTH_TYPE_SELECTION'));
		}
		setPendingAction(null);
	};

	return (
		<div id="pos-ident-redirect-step">
			<Loading>
				<p className="whitespace-pre-line">{t(`${TRANSLATION_PREFIX}.preparing`)}</p>
			</Loading>

			{showRecovery && (
				<div className="flex flex-col items-stretch gap-3 w-full mt-4 px-2">
					<StepButton
						disabled={!redirectUrl}
						title={t(`${TRANSLATION_PREFIX}.reopen`)}
						onClick={() => setPendingAction('reopen')}
					/>
					<button
						onClick={() => setPendingAction('back')}
						className="text-sm font-[500] text-gray-700 underline hover:text-gray-900 focus:outline-none"
					>
						{t(`${TRANSLATION_PREFIX}.back-to-selection`)}
					</button>
				</div>
			)}

			{showRecovery && pendingAction && (
				<PosIdentConfirmModal
					title={t(`${TRANSLATION_PREFIX}.confirm-title`)}
					message={t(`${TRANSLATION_PREFIX}.confirm-message`)}
					warning={t(`${TRANSLATION_PREFIX}.confirm-warning`)}
					waitLabel={t(`${TRANSLATION_PREFIX}.confirm-wait`)}
					confirmLabel={t(`${TRANSLATION_PREFIX}.${pendingAction === 'reopen' ? 'reopen' : 'back-to-selection'}`)}
					onConfirm={confirmAction}
					onCancel={() => setPendingAction(null)}
				/>
			)}
		</div>
	);
};

export default PosIdentRedirect;
